import { createContext, useContext } from 'react';
import type { MixState, PresetName, Skin, SkyblogTheme, SteveState } from './types';

/**
 * Tout ce que les skins peuvent déclencher. Aucun skin ne touche l'état
 * directement.
 *
 * La langue n'y figure pas : elle suit le navigateur et ne change plus. Le skin,
 * lui, n'a qu'une commande — les flèches du webring, en bas du skin y2k — en
 * plus du tirage au chargement et du mot secret.
 */
export interface SteveActions {
  /** Clic sur Steve : incrémente le compteur du jour. */
  bump: () => void;
  /**
   * Tourne d'un cran dans l'anneau des skins : -1 précédent, +1 suivant,
   * 0 au hasard (mais jamais le skin courant).
   */
  cycleSkin: (step: -1 | 0 | 1) => void;
  /** Va droit à un skin : seul le shell du skin matrix s'en sert. */
  setSkin: (skin: Skin) => void;
  /** Repeint le skyblog : mémorisé, et noté dans le journal partagé. */
  setSkyblogTheme: (theme: SkyblogTheme) => void;
  /**
   * Adopte un pseudo kikoo, ou redevient anonyme avec une chaîne vide.
   *
   * Le générateur du skyblog est le seul à l'appeler, mais son effet est
   * global : c'est la signature du visiteur dans le livre d'or des onze skins.
   */
  setKikoo: (name: string) => void;
  /** Décolle un poisson en papier. Il n'y a pas de commande pour en coller un. */
  peelFish: (id: number) => void;
  feed: () => void;
  praise: () => void;
  bubbles: () => void;
  toggleAudio: () => void;
  hitPad: (index: number) => void;
  stopPad: () => void;
  toggleHold: () => void;
  /** Steve-curseur : allumé par défaut, coupable depuis le menu Steve. */
  toggleFollower: () => void;
  setMix: (patch: Partial<MixState>) => void;
  preset: (name: PresetName) => void;
  toggleWobble: () => void;
  setDraft: (draft: string) => void;
  postEntry: () => void;
  openEgg: () => void;
  closeEgg: () => void;
  /** Remplit un tableau de niveaux 0..1 pour le VU-mètre. */
  readBars: (out: Float32Array) => boolean;
}

export const StateContext = createContext<SteveState | null>(null);
export const ActionsContext = createContext<SteveActions | null>(null);

export function useSteve(): SteveState {
  const state = useContext(StateContext);
  if (!state) throw new Error('useSteve doit être utilisé dans <SteveProvider>');
  return state;
}

export function useActions(): SteveActions {
  const actions = useContext(ActionsContext);
  if (!actions) throw new Error('useActions doit être utilisé dans <SteveProvider>');
  return actions;
}
